import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ResolutionTraceField.css";

const STATUS_LABELS = {
  resolved_single_admitted_configuration: "coherent",
  resolved_by_evidence_specificity: "resolved",
  unresolved_insufficient_configuration_differentiation: "parallel",
  unresolved_no_admitted_specific_configuration: "open",
};

const STATUS_ORDER = [
  "resolved_single_admitted_configuration",
  "resolved_by_evidence_specificity",
  "unresolved_insufficient_configuration_differentiation",
  "unresolved_no_admitted_specific_configuration",
];

function clamp(v, lo = 0, hi = 1) {
  return Math.max(lo, Math.min(hi, Number(v) || 0));
}

function hash01(input) {
  let h = 2166136261;
  const s = String(input || "");
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

function pretty(value = "") {
  return String(value)
    .replace(/^capability:/, "")
    .replace(/^CFG:/, "")
    .replaceAll("_", " ");
}

function shortCapability(value = "") {
  const p = pretty(value);
  return p.length > 35 ? `${p.slice(0, 34)}…` : p;
}

function fieldVector(x, y, width, height, active, tSeed) {
  const nx = x / Math.max(width, 1);
  const ny = y / Math.max(height, 1);

  let angle =
    -0.18 +
    Math.sin(nx * 7.2 + tSeed * 3.0) * 0.035 +
    Math.cos(ny * 8.8 - tSeed * 2.2) * 0.027;

  if (active?.__focus) {
    const dx = active.__focus.x - x;
    const dy = active.__focus.y - y;
    const d = Math.hypot(dx, dy);
    const influence = Math.max(0, 1 - d / Math.max(width, height) * 2.25);

    if (influence > 0) {
      const toward = Math.atan2(dy, dx);
      const status = active.resolution_status;

      if (
        status === "resolved_single_admitted_configuration" ||
        status === "resolved_by_evidence_specificity"
      ) {
        angle = angle * (1 - influence * 0.68) + toward * (influence * 0.68);
      } else if (status === "unresolved_insufficient_configuration_differentiation") {
        angle += Math.sin((x + y) * 0.018) * influence * 0.2;
      } else {
        angle += Math.cos(y * 0.025) * influence * 0.08;
      }
    }
  }

  return angle;
}

function drawStreamline(ctx, startX, startY, width, height, active, seed, alpha, weight) {
  const steps = 42;
  const step = 8;
  let x = startX;
  let y = startY;

  ctx.beginPath();
  ctx.moveTo(x, y);

  for (let i = 0; i < steps; i += 1) {
    const angle = fieldVector(x, y, width, height, active, seed);
    x += Math.cos(angle) * step;
    y += Math.sin(angle) * step;

    if (x < -30 || x > width + 30 || y < -30 || y > height + 30) break;
    ctx.lineTo(x, y);
  }

  ctx.strokeStyle = `rgba(17, 70, 255, ${alpha})`;
  ctx.lineWidth = weight;
  ctx.stroke();
}

function TraceCanvas({ cards, activeId, onSelect }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;

    const parent = canvas.parentElement;
    if (!parent) return undefined;

    let raf = 0;

    const draw = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = parent.getBoundingClientRect();
      const width = Math.max(520, Math.floor(rect.width));
      const height = Math.max(620, Math.floor(rect.height));

      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      const active = cards.find((c) => c.resolution_id === activeId) || cards[0];

      // Deterministic atlas positions, slightly radial rather than grid-like.
      const cx = width * 0.50;
      const cy = height * 0.49;
      const rx = width * 0.40;
      const ry = height * 0.34;

      cards.forEach((card, index) => {
        const a = hash01(`${card.candidate_id}:a`) * Math.PI * 2;
        const r = Math.sqrt(hash01(`${card.candidate_id}:r`));
        const jitterX = (hash01(`${card.candidate_id}:jx`) - 0.5) * 24;
        const jitterY = (hash01(`${card.candidate_id}:jy`) - 0.5) * 24;

        card.__focus = {
          x: cx + Math.cos(a) * rx * r + jitterX,
          y: cy + Math.sin(a) * ry * r + jitterY,
        };
      });

      // Pale paper / atmospheric field.
      const grad = ctx.createRadialGradient(
        width * 0.49,
        height * 0.46,
        20,
        width * 0.49,
        height * 0.46,
        Math.max(width, height) * 0.72
      );
      grad.addColorStop(0, "rgba(255,255,255,0.94)");
      grad.addColorStop(0.56, "rgba(248,248,244,0.90)");
      grad.addColorStop(1, "rgba(239,240,235,0.94)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Organic substrate traces.
      const traceGap = 7.5;
      for (let y = -30; y < height + 30; y += traceGap) {
        const seed = hash01(`substrate:${Math.round(y)}`);
        drawStreamline(
          ctx,
          -26,
          y + (seed - 0.5) * 12,
          width,
          height,
          active,
          seed,
          0.12,
          0.7
        );
      }

      // Secondary faint cross-current to avoid ruled-paper effect.
      ctx.save();
      ctx.globalCompositeOperation = "multiply";
      for (let y = 18; y < height; y += 28) {
        const seed = hash01(`secondary:${y}`);
        drawStreamline(
          ctx,
          width * 0.18,
          y,
          width,
          height,
          active,
          seed + 0.31,
          0.045,
          0.55
        );
      }
      ctx.restore();

      // Local formations from actual ResolutionCards.
      cards.forEach((card) => {
        const p = card.__focus;
        if (!p) return;

        const activeHere = card.resolution_id === activeId;
        const specificity = clamp(card.evidence_specificity);
        const differentiation = clamp(Math.abs(Number(card.resolution_margin) || 0) * 8);

        let radius = 14 + specificity * 10;
        let strands = 2;
        let spread = 0.22;
        let opacity = 0.24;

        if (card.resolution_status === "resolved_single_admitted_configuration") {
          radius += 8;
          strands = 3;
          spread = 0.07;
          opacity = 0.52;
        } else if (card.resolution_status === "resolved_by_evidence_specificity") {
          radius += 11;
          strands = 4;
          spread = 0.12 + (1 - differentiation) * 0.08;
          opacity = 0.46;
        } else if (card.resolution_status === "unresolved_insufficient_configuration_differentiation") {
          radius += 5;
          strands = 4;
          spread = 0.33;
          opacity = 0.27;
        } else {
          strands = 2;
          spread = 0.52;
          opacity = 0.12;
        }

        // Soft local field veil instead of point marker.
        const local = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius * 2.6);
        local.addColorStop(0, activeHere ? "rgba(0,42,255,.11)" : "rgba(0,42,255,.035)");
        local.addColorStop(1, "rgba(0,42,255,0)");
        ctx.fillStyle = local;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius * 2.6, 0, Math.PI * 2);
        ctx.fill();

        for (let s = 0; s < strands; s += 1) {
          const offset = strands === 1 ? 0 : s / (strands - 1) - 0.5;
          const baseA = -0.22 + (hash01(`${card.candidate_id}:la`) - 0.5) * 0.18;
          const a = baseA + offset * spread;
          const len = radius * (2.4 + hash01(`${card.candidate_id}:${s}`) * 0.7);

          ctx.beginPath();
          ctx.moveTo(
            p.x - Math.cos(a) * len * 0.5,
            p.y - Math.sin(a) * len * 0.5
          );
          ctx.quadraticCurveTo(
            p.x + offset * radius * 0.42,
            p.y - radius * 0.18,
            p.x + Math.cos(a) * len * 0.5,
            p.y + Math.sin(a) * len * 0.5
          );
          ctx.strokeStyle = activeHere
            ? "rgba(0,42,255,.95)"
            : `rgba(0,58,240,${opacity})`;
          ctx.lineWidth = activeHere ? 2.15 : 1.0;
          ctx.stroke();
        }

        if (activeHere) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 5.2, 0, Math.PI * 2);
          ctx.fillStyle = "#f8f8f3";
          ctx.fill();
          ctx.strokeStyle = "rgba(0,42,255,.95)";
          ctx.lineWidth = 1.7;
          ctx.stroke();
        }

        card.__hit = { x: p.x, y: p.y, r: Math.max(16, radius * 1.7) };
      });

      // Central active relation anchor.
      if (active?.__focus) {
        const p = active.__focus;
        const anchorX = width * 0.5;
        const anchorY = height * 0.5;

        ctx.save();
        ctx.strokeStyle = "rgba(0,42,255,.24)";
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 5]);
        ctx.beginPath();
        ctx.moveTo(anchorX, anchorY);
        ctx.quadraticCurveTo(
          (anchorX + p.x) * 0.5,
          Math.min(anchorY, p.y) - 28,
          p.x,
          p.y
        );
        ctx.stroke();
        ctx.restore();

        const anchorGrad = ctx.createRadialGradient(
          anchorX,
          anchorY,
          4,
          anchorX,
          anchorY,
          72
        );
        anchorGrad.addColorStop(0, "rgba(255,255,252,.98)");
        anchorGrad.addColorStop(0.64, "rgba(248,248,243,.94)");
        anchorGrad.addColorStop(1, "rgba(248,248,243,0)");

        ctx.fillStyle = anchorGrad;
        ctx.beginPath();
        ctx.arc(anchorX, anchorY, 72, 0, Math.PI * 2);
        ctx.fill();

        ctx.beginPath();
        ctx.arc(anchorX, anchorY, 31, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(250,250,246,.96)";
        ctx.fill();
        ctx.strokeStyle = "rgba(22,45,57,.18)";
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.fillStyle = "#15333f";
        ctx.font = "600 10px Inter, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("ACTIVE", anchorX, anchorY - 4);
        ctx.font = "10px Georgia, serif";
        ctx.fillText("resolution", anchorX, anchorY + 11);
      }
    };

    const resize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    };

    draw();
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [cards, activeId]);

  const handleClick = (event) => {
    const canvas = ref.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    let best = null;
    let bestD = Infinity;
    cards.forEach((card) => {
      const hit = card.__hit;
      if (!hit) return;
      const d = Math.hypot(x - hit.x, y - hit.y);
      if (d < hit.r && d < bestD) {
        best = card;
        bestD = d;
      }
    });

    if (best) onSelect(best.resolution_id);
  };

  return (
    <canvas
      ref={ref}
      className="resolution-trace-canvas"
      onClick={handleClick}
      aria-label="Resolution trace field"
    />
  );
}

function Metric({ label, value }) {
  return (
    <div className="resolution-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default function ResolutionTraceField({
  dataUrl = "/resolution-field/resolution_cards_v25_6_1.jsonl",
}) {
  const [cards, setCards] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    fetch(dataUrl)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        const rows = text
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean)
          .map((line) => JSON.parse(line));
        setCards(rows);
        setActiveId(rows.find((r) => r.resolved)?.resolution_id || rows[0]?.resolution_id || "");
      })
      .catch((err) => {
        if (!cancelled) setError(String(err?.message || err));
      });

    return () => {
      cancelled = true;
    };
  }, [dataUrl]);

  const counts = useMemo(() => {
    const next = Object.fromEntries(STATUS_ORDER.map((s) => [s, 0]));
    cards.forEach((card) => {
      if (card.resolution_status in next) next[card.resolution_status] += 1;
    });
    return next;
  }, [cards]);

  const visibleCards = useMemo(
    () =>
      statusFilter === "all"
        ? cards
        : cards.filter((card) => card.resolution_status === statusFilter),
    [cards, statusFilter]
  );

  const active = useMemo(
    () =>
      cards.find((card) => card.resolution_id === activeId) ||
      visibleCards[0] ||
      null,
    [cards, activeId, visibleCards]
  );

  const setFilter = (value) => {
    setStatusFilter(value);
    const next =
      value === "all"
        ? cards.find((card) => card.resolved) || cards[0]
        : cards.find((card) => card.resolution_status === value);
    if (next) setActiveId(next.resolution_id);
  };

  return (
    <main className="resolution-field-shell">
      <div className="resolution-field-topbar">
        <div className="resolution-brand">
          <span className="resolution-brand-mark">△</span>
          <strong>Pathfinder</strong>
        </div>
        <div className="resolution-breadcrumb">
          Configurations <span>→</span> Resolution field
        </div>
        <div className="resolution-version">V25.6.2 prototype</div>
      </div>

      <section className="resolution-field-intro">
        <div>
          <span className="resolution-eyebrow">Understand one configuration</span>
          <h1>Resolution field</h1>
          <p>
            Read how admitted configuration–capability relations become more or less
            differentiated through the evidence field.
          </p>
        </div>

        <div className="resolution-summary-strip">
          <Metric label="resolutions" value={cards.length || "—"} />
          <Metric label="pathways" value={cards.filter((c) => c.resolved).length || "—"} />
          <Metric label="identities" value="12" />
        </div>
      </section>

      <nav className="resolution-field-filters" aria-label="Resolution field filters">
        <button
          type="button"
          className={statusFilter === "all" ? "is-active" : ""}
          onClick={() => setFilter("all")}
        >
          all <span>{cards.length}</span>
        </button>
        {STATUS_ORDER.map((status) => (
          <button
            type="button"
            key={status}
            className={statusFilter === status ? "is-active" : ""}
            onClick={() => setFilter(status)}
          >
            {STATUS_LABELS[status]} <span>{counts[status] || 0}</span>
          </button>
        ))}
      </nav>

      {error ? (
        <div className="resolution-field-error">
          Could not load the V25.6.1 read model: {error}
        </div>
      ) : (
        <section className="resolution-atlas">
          <div className="resolution-field-stage">
            <TraceCanvas
              cards={visibleCards}
              activeId={active?.resolution_id}
              onSelect={setActiveId}
            />

            {active ? (
              <div className="resolution-active-label">
                <span>{STATUS_LABELS[active.resolution_status]}</span>
                <strong>{shortCapability(active.capability_id)}</strong>
                <small>
                  {active.resolved_configuration_id
                    ? pretty(active.resolved_configuration_id)
                    : "unresolved field"}
                </small>
              </div>
            ) : null}

            <div className="resolution-field-legend">
              <span><i className="legend-line coherent" /> coherent orientation</span>
              <span><i className="legend-line parallel" /> unresolved parallelism</span>
              <span><i className="legend-line open" /> open field</span>
            </div>
          </div>

          {active ? (
            <aside className="resolution-bottom-inspector">
              <div className="resolution-inspector-media">
                <div className={`resolution-status-orb ${active.trace_field?.mode || ""}`}>
                  <span />
                </div>
              </div>

              <div className="resolution-inspector-main">
                <span className="resolution-eyebrow">
                  {STATUS_LABELS[active.resolution_status]}
                </span>
                <h2>{pretty(active.capability_id)}</h2>
                <p>
                  {active.resolved_configuration_id
                    ? `This trace resolves through ${pretty(active.resolved_configuration_id)}.`
                    : "The evidence remains unresolved within the admitted field."}
                </p>
              </div>

              <div className="resolution-inspector-metrics">
                <Metric
                  label="specificity"
                  value={
                    active.evidence_specificity == null
                      ? "—"
                      : Number(active.evidence_specificity).toFixed(3)
                  }
                />
                <Metric
                  label="resolution margin"
                  value={
                    active.resolution_margin == null
                      ? "—"
                      : Number(active.resolution_margin).toFixed(3)
                  }
                />
                <Metric
                  label="admitted field"
                  value={active.admitted_configuration_count}
                />
              </div>

              <div className="resolution-inspector-provenance">
                <span className="resolution-eyebrow">Trace provenance</span>
                <code>{active.root_id}</code>
                <code>{active.relational_atom_id}</code>
                {active.identity_projection_id ? <code>{active.identity_projection_id}</code> : null}
                {active.pathway_id ? <code>{active.pathway_id}</code> : null}
              </div>
            </aside>
          ) : null}
        </section>
      )}
    </main>
  );
}
